import { useEffect, useRef, useMemo, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useEditor, EditorContent } from '@tiptap/react';
import { BubbleMenu } from '@tiptap/react/menus';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import CharacterCount from '@tiptap/extension-character-count';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import Highlight from '@tiptap/extension-highlight';
import Link from '@tiptap/extension-link';
import Typography from '@tiptap/extension-typography';
import { TextStyle } from '@tiptap/extension-text-style';
import Color from '@tiptap/extension-color';
import Dropcursor from '@tiptap/extension-dropcursor';
import { Mark, mergeAttributes } from '@tiptap/core';
import Image from '@tiptap/extension-image';
import { marked } from 'marked';
import { notify } from '../lib/toast';
import { api, AiLimitError } from '../lib/api';
import type { BibleEntry, BibleEntryType } from '../lib/api';
import type { TFunction } from 'i18next';
import { CodexHighlight } from '../lib/codex-highlight';
import { SearchHighlight } from '../lib/search-highlight';
import { AiAutocomplete } from './extensions/ai-autocomplete';
import { EditorSmoke, type SmokeHandle } from './editor-smoke';
import { CodexPreviewCard } from './codex-preview-card';
import { EntryDossier } from './entry-dossier';
import { MobileEditorToolbar } from './mobile-editor-toolbar';
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  Strikethrough,
  Highlighter,
  AlignLeft,
  AlignCenter,
  AlignRight,
  AlignJustify,
  Heading2,
  Heading3,
  Link as LinkIcon,
  Quote,
  List,
  ListOrdered,
  RemoveFormatting,
  MessageSquarePlus,
  MessageSquare,
  ImagePlus,
  X,
  Circle,
  Square,
  Triangle,
  Hexagon,
  RotateCw,
  RectangleHorizontal,
  RectangleVertical,
  ChevronDown,
  Sparkles,
  Loader2,
} from 'lucide-react';
import { AIMenu } from './ai';
import { Badge } from './ui/badge';
import { PresenceAvatars } from './presence-avatars';
import type { Chapter } from '../types';
import { PAGES_WITH_IMAGE } from '../types';
import { useAppStore } from '../stores/app-store';
import { cn } from '../lib/utils';
import { motion } from 'motion/react';
import { MoonLoader } from './ui/moon-loader';
import { useIsMobile } from '../hooks/use-mobile';
import type { Extension } from '@tiptap/core';
import type { WebsocketProvider } from 'y-websocket';

// Custom Comment Mark extension
const CommentMark = Mark.create({
  name: 'comment',

  addAttributes() {
    return {
      commentId: { default: null },
      commentText: { default: '' },
    };
  },

  parseHTML() {
    return [{ tag: 'span[data-comment]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'span',
      mergeAttributes(HTMLAttributes, {
        'data-comment': HTMLAttributes.commentId,
        class: 'comment-highlight',
      }),
      0,
    ];
  },
});

/** Translated label for a bible entry type (mirrors story-bible-view's getEntryTypeLabel). */
function getEntryTypeLabel(t: TFunction, type: BibleEntryType): string {
  switch (type) {
    case 'character':
      return t('storyBible.entryTypeCharacter', 'Personagem');
    case 'location':
      return t('storyBible.entryTypeLocation', 'Local');
    case 'lore':
      return t('storyBible.entryTypeLore', 'Lore');
    case 'item':
      return t('storyBible.entryTypeItem', 'Item');
    case 'organization':
      return t('storyBible.entryTypeOrganization', 'Organização');
    case 'note':
      return t('storyBible.entryTypeNote', 'Nota');
    default:
      return type;
  }
}

/** Static per-chapter identity + live callbacks needed by the AI autocomplete plugin. */
interface AiAutocompleteWiring {
  projectId: string;
  chapterId: string;
  onSuggestionChange: (suggestion: string | null, from: number | null) => void;
  onQuotaExceeded: () => void;
  onPendingChange: (pending: boolean) => void;
}

// Base extensions shared between solo and collaborative modes.
// These never include StarterKit.undoRedo/history — that's set per-mode below.
// Placeholder text is injected per-call (see buildExtensions) so it can be
// localized via t() — the array itself lives outside the component (no `t` in scope).
function buildBaseExtensions(placeholderText: string, aiWiring: AiAutocompleteWiring) {
  return [
  Placeholder.configure({
    placeholder: placeholderText,
  }),
  CharacterCount,
  Underline,
  TextAlign.configure({
    types: ['heading', 'paragraph'],
    alignments: ['left', 'center', 'right', 'justify'],
    defaultAlignment: 'left',
  }),
  Highlight.configure({ multicolor: true }),
  Link.configure({
    openOnClick: false,
    HTMLAttributes: { class: 'text-primary underline hover:text-primary/80' },
  }),
  Typography,
  TextStyle,
  Color,
  Dropcursor.configure({ color: 'hsl(var(--primary))', width: 2 }),
  CommentMark,
  Image.configure({ inline: false, allowBase64: true }),
  // Codex Inline: highlights story-bible entity names with non-destructive
  // decorations. Entries are pushed in via the setCodexEntries command after
  // the editor mounts, so it starts empty and has zero overhead when unused.
  CodexHighlight,
  // Search Highlight: non-destructive Find/Replace match decorations.
  // Matches are pushed via setSearchMatches; never steals focus.
  SearchHighlight,
  // Ghost-text continuation suggestions. Starts disabled — actual on/off and
  // length are pushed live via setAiAutocompleteSettings (see effect below),
  // so toggling the toolbar switch never requires recreating the editor.
  AiAutocomplete.configure({
    projectId: aiWiring.projectId,
    chapterId: aiWiring.chapterId,
    onSuggestionChange: aiWiring.onSuggestionChange,
    onQuotaExceeded: aiWiring.onQuotaExceeded,
    onPendingChange: aiWiring.onPendingChange,
  }),
  ];
}

// Solo-mode StarterKit (with native undo/redo)
const SOLO_STARTER_KIT = StarterKit.configure({
  heading: { levels: [1, 2, 3] },
  bulletList: { keepMarks: true, keepAttributes: false },
  orderedList: { keepMarks: true, keepAttributes: false },
  dropcursor: false,
  link: false,
  underline: false,
  undoRedo: { depth: 200, newGroupDelay: 500 },
});

// Collaborative-mode StarterKit (undoRedo disabled — Collaboration/yUndoPlugin handles it)
const COLLAB_STARTER_KIT = StarterKit.configure({
  heading: { levels: [1, 2, 3] },
  bulletList: { keepMarks: true, keepAttributes: false },
  orderedList: { keepMarks: true, keepAttributes: false },
  dropcursor: false,
  link: false,
  underline: false,
  // Disable native undo/redo: Collaboration extension (yUndoPlugin) handles it
  undoRedo: false,
});

/** Build the full extension list for the editor. */
function buildExtensions(
  collaborationExtensions: Extension[] | undefined,
  placeholderText: string,
  aiWiring: AiAutocompleteWiring,
): Extension[] {
  const baseExtensions = buildBaseExtensions(placeholderText, aiWiring);
  if (collaborationExtensions && collaborationExtensions.length > 0) {
    return [
      COLLAB_STARTER_KIT as unknown as Extension,
      ...baseExtensions as unknown as Extension[],
      ...collaborationExtensions,
    ];
  }
  return [
    SOLO_STARTER_KIT as unknown as Extension,
    ...baseExtensions as unknown as Extension[],
  ];
}

// Convert markdown to HTML using marked library
/** Chapter number as a roman numeral — "Capítulo XII", like the approved design. */
function toRoman(n: number): string {
  if (n <= 0 || n >= 4000) return String(n);
  const table: [number, string][] = [
    [1000, 'M'], [900, 'CM'], [500, 'D'], [400, 'CD'],
    [100, 'C'], [90, 'XC'], [50, 'L'], [40, 'XL'],
    [10, 'X'], [9, 'IX'], [5, 'V'], [4, 'IV'], [1, 'I'],
  ];
  let out = '';
  let v = n;
  for (const [val, sym] of table) {
    while (v >= val) { out += sym; v -= val; }
  }
  return out;
}

function parseMarkdownToHtml(content: string): string {
  if (!content || content.trim() === '') return '';

  // If content is already pure HTML (starts with tag and has closing tags), return as-is
  const trimmed = content.trim();
  if (trimmed.startsWith('<') && (trimmed.includes('</p>') || trimmed.includes('</h1>') || trimmed.includes('</h2>') || trimmed.includes('</div>'))) {
    return content;
  }

  // Use marked library for robust markdown parsing
  marked.setOptions({
    breaks: true,
    gfm: true,
  });

  return marked.parse(content, { async: false }) as string;
}

interface CollaborationProps {
  /** Tiptap extensions from useCollaboration (Collaboration + cursor plugin) */
  extensions: Extension[];
  /** Remote users currently editing */
  remoteUsers: Array<{ clientId: number; name: string; color: string }>;
  /** Whether the WebSocket is connected */
  connected: boolean;
  /** y-websocket provider — to seed the Y.Doc safely once it syncs. */
  provider: WebsocketProvider;
}

interface ChapterEditorProps {
  chapter: Chapter;
  projectId: string;
  chapterNumber?: number;
  onContentChange: (content: string) => void;
  onTitleChange: (title: string) => void;
  onImageChange?: (image: string | undefined) => void;
  focusMode?: boolean;
  onEditorReady?: (editor: ReturnType<typeof useEditor>) => void;
  onAddComment?: (commentText: string, selectedText: string, position: { from: number; to: number }) => void;
  onToggleComments?: () => void;
  commentsOpen?: boolean;
  commentCount?: number;
  saveStatus?: 'saved' | 'saving' | 'unsaved';
  onSelectionChange?: (text: string, range: { from: number; to: number } | null) => void;
  /** When present, enables collaborative editing for this chapter */
  collaboration?: CollaborationProps;
  /** Sibling chapter titles — used so the AI title suggestion matches the book's naming style. */
  siblingTitles?: string[];
}

export function ChapterEditor({
  chapter,
  projectId,
  chapterNumber,
  onContentChange,
  onTitleChange,
  onImageChange,
  focusMode = false,
  onEditorReady,
  onAddComment,
  onToggleComments,
  commentsOpen = false,
  commentCount = 0,
  saveStatus = 'saved',
  onSelectionChange,
  collaboration,
  siblingTitles,
}: ChapterEditorProps) {
  const { t } = useTranslation();
  const titleRef = useRef<HTMLInputElement>(null);
  const updateTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  // True once the collaborative Y.Doc has synced with the server — gates the
  // autosave so a not-yet-synced (empty) editor can never erase chapter.content.
  const collabReadyRef = useRef(false);
  // Tracks whether the last selection was non-empty, so we don't fire the
  // "no selection" callback on every keystroke (the cursor moves each key).
  const lastHadSelectionRef = useRef(false);
  // Live ink-smoke layer over the writing canvas.
  const smokeRef = useRef<SmokeHandle>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const settings = useAppStore((s) => s.settings);
  const isMobile = useIsMobile();
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [showAIMenu, setShowAIMenu] = useState(false);
  const [isSuggestingTitle, setIsSuggestingTitle] = useState(false);
  // Current ghost-text suggestion (mirrors the ai-autocomplete plugin state) —
  // used to render the mobile "Tab to accept" pill near the caret.
  const [aiSuggestion, setAiSuggestion] = useState<{ text: string; from: number } | null>(null);
  // True while an autocomplete request is actually in flight — drives the
  // subtle "thinking" indicator near the caret (desktop + mobile).
  const [aiPending, setAiPending] = useState(false);
  // Stable callback identity for the extension — always reads the latest setter
  // via a ref so the editor doesn't need to be recreated when this changes.
  const aiSuggestionChangeRef = useRef((suggestion: string | null, from: number | null) => {
    setAiSuggestion(suggestion && from !== null ? { text: suggestion, from } : null);
  });
  const aiPendingChangeRef = useRef((pending: boolean) => setAiPending(pending));
  // Quota-exceeded is reported by the extension at most once per editor
  // instance already — this ref just keeps the callback identity stable
  // while always reading the latest `t` (language can change mid-session).
  const aiQuotaExceededRef = useRef(() => {});
  aiQuotaExceededRef.current = () => {
    notify.warning(t('chapterEditor.aiAutocompletePaused', 'Cota de IA esgotada — sugestões pausadas'));
  };
  // Inline comment composer (replaces the old window.prompt)
  const [commentDraft, setCommentDraft] = useState<{
    from: number;
    to: number;
    selectedText: string;
    style: React.CSSProperties;
  } | null>(null);
  const [commentText, setCommentText] = useState('');

  // Image editor states
  const [imageShape, setImageShape] = useState<'square' | 'circle' | 'triangle' | 'hexagon' | 'diamond'>('square');
  const [imageAspect, setImageAspect] = useState<'square' | 'portrait' | 'landscape'>('square');
  const [shapeRotation, setShapeRotation] = useState(0);
  const [borderRadius, setBorderRadius] = useState(0);
  const [showImageControls, setShowImageControls] = useState(false);
  const [imageZoom, setImageZoom] = useState(1.4);
  const [imageFilter, setImageFilter] = useState<'none' | 'grayscale' | 'sepia' | 'contrast' | 'brightness' | 'gradient-dark' | 'gradient-light'>('none');
  const imageContainerRef = useRef<HTMLDivElement>(null);

  // Bible entries for Codex Inline highlighting
  const [bibleEntries, setBibleEntries] = useState<BibleEntry[]>([]);
  // Entry whose full dossier is currently open (read-only, opened via click on a codex entity)
  const [dossierEntry, setDossierEntry] = useState<BibleEntry | null>(null);
  const [popoverPosition, setPopoverPosition] = useState({ top: 0, left: 0 });

  // Check if this page type supports images
  const supportsImage = PAGES_WITH_IMAGE.includes(chapter.sectionType);

  // Parse initial content — only used in solo mode (collaboration uses Y.Doc state)
  const initialContent = useMemo(() => parseMarkdownToHtml(chapter.content), [chapter.id]);

  // Build extensions once per chapter + collab state combination.
  // In collaborative mode: Tiptap/Yjs will seed content from the Y.Doc, so we
  // pass empty string to avoid overwriting the shared doc state.
  const extensions = useMemo(
    () =>
      buildExtensions(collaboration?.extensions, t('chapterEditor.startWriting', 'Comece a escrever...'), {
        projectId,
        chapterId: chapter.id,
        onSuggestionChange: (suggestion, from) => aiSuggestionChangeRef.current(suggestion, from),
        onQuotaExceeded: () => aiQuotaExceededRef.current(),
        onPendingChange: (pending) => aiPendingChangeRef.current(pending),
      }),
    // Re-build only when chapter changes or collaboration is toggled on/off
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [chapter.id, !!collaboration, t, projectId],
  );

  const editor = useEditor(
    {
      extensions,
      // Collaborative mode: start EMPTY and let the explicit synced-seed effect
      // fill the Y.Doc from chapter.content only if it's still empty after sync.
      // (Passing content here would auto-seed on creation, before the sync check,
      // and duplicate content across devices.) Solo mode: pass the HTML directly.
      content: collaboration ? '' : initialContent,
      editable: true,
      // Do NOT auto-focus: grabbing focus on mount pops the mobile keyboard.
      // Focus only happens when the user taps/clicks the contenteditable directly.
      autofocus: false,
      immediatelyRender: false, // Required for React 18 StrictMode
      editorProps: {
        attributes: {
          class: cn(
            'editor-content max-w-none focus:outline-none min-h-[50vh]',
            settings.fontFamily === 'sans'
              ? 'font-sans'
              : settings.fontFamily === 'mono'
                ? 'font-mono'
                : 'font-serif',
            settings.dropCaps && 'drop-caps'
          ),
          spellcheck: settings.spellcheck ? 'true' : 'false',
        },
      },
      onUpdate: ({ editor: ed }) => {
        // SAFETY: in collaborative mode, never autosave until the Y.Doc has
        // synced with the server. Before sync the editor is intentionally empty
        // — saving that would ERASE chapter.content. After sync it holds the real
        // content (server state or the seed), so saving is safe.
        if (collaboration && !collabReadyRef.current) return;
        if (updateTimeoutRef.current) {
          clearTimeout(updateTimeoutRef.current);
        }
        updateTimeoutRef.current = setTimeout(() => {
          onContentChange(ed.getHTML());
        }, 300);
      },
      onSelectionUpdate: ({ editor: ed }) => {
        if (!onSelectionChange) return;
        const { from, to } = ed.state.selection;
        const hasSel = from !== to;
        // Skip the redundant "no selection" call fired on every keystroke.
        if (!hasSel && !lastHadSelectionRef.current) return;
        lastHadSelectionRef.current = hasSel;
        if (hasSel) {
          onSelectionChange(ed.state.doc.textBetween(from, to, ' '), { from, to });
        } else {
          onSelectionChange('', null);
        }
      },
      // Live ink-smoke: puff a wisp at the caret whenever text is inserted.
      // Cheap — coordsAtPos + a few canvas particles; no React re-render.
      onTransaction: ({ editor: ed, transaction: tr }) => {
        if (!tr.docChanged) return;
        const inserted = tr.doc.content.size - tr.before.content.size;
        if (inserted <= 0 || inserted > 12) return; // typing / tiny edits only
        try {
          const c = ed.view.coordsAtPos(ed.state.selection.head);
          // Emit from the VERTICAL CENTRE of the character line (not c.top, which
          // is the line top and made effects appear above the letter).
          smokeRef.current?.emit(c.left, (c.top + c.bottom) / 2);
        } catch {
          /* view not ready — ignore */
        }
      },
    },
    // Recreate editor when chapter changes or when collaboration mode toggles
    [chapter.id, !!collaboration],
  );

  // Update editor content when chapter changes.
  // In collaborative mode, the Y.Doc/ySyncPlugin manages content — we only scroll.
  useEffect(() => {
    if (!editor) return;

    if (!collaboration) {
      // Solo mode: push content from the chapter data
      const htmlContent = parseMarkdownToHtml(chapter.content);
      editor.commands.setContent(htmlContent, { emitUpdate: false });
    }

    // The ProseMirror view may not be mounted yet on this commit (effect
    // ordering vs <EditorContent/>). Accessing `editor.view` throws when the
    // view isn't available — so defer the scroll reset to the next frame and
    // guard it. Without this, creating/switching a chapter crashes the editor.
    // NOTE: we intentionally do NOT call focus() here — doing so pops the mobile
    // keyboard on chapter switch. Only a real user tap focuses the editor.
    const raf = requestAnimationFrame(() => {
      if (!editor || editor.isDestroyed) return;
      try {
        const dom = editor.view.dom;
        const editorElement = dom.closest('.editor-content');
        if (editorElement) (editorElement as HTMLElement).scrollTop = 0;
        const scrollContainer = dom.closest('[class*="overflow"]');
        if (scrollContainer) (scrollContainer as HTMLElement).scrollTop = 0;
      } catch {
        /* editor view not mounted yet — safe to skip the scroll reset */
      }
    });

    return () => cancelAnimationFrame(raf);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chapter.id, editor]);

  // Collaborative mode: SEED the Y.Doc from the saved chapter.content ONCE the
  // provider has synced with the server — but only if the doc is still empty
  // (server/other clients had nothing yet). This is what makes real-time solo
  // safe: the editor never shows blank and the autosave never erases the book.
  // If the server already has state, the fragment is non-empty and we skip.
  const collabSeededRef = useRef(false);
  // In collab mode we hold the ProseMirror VIEW back until the Y.Doc has synced,
  // then mount it. Creating the view only once the doc holds its real content is
  // what fixes the mobile "chapter opens blank until you switch chapters" bug:
  // otherwise the view mounts against an empty doc and, on mobile, doesn't always
  // re-render when the fragment fills. Solo mode mounts immediately.
  const [collabSynced, setCollabSynced] = useState(false);
  useEffect(() => {
    if (!collaboration || !editor) { setCollabSynced(false); return; }
    const provider = collaboration.provider;
    collabSeededRef.current = false;
    collabReadyRef.current = false;
    setCollabSynced(false);
    let done = false;
    const ready = () => {
      if (done || editor.isDestroyed) return;
      done = true;
      // Synced (or timed out) → the doc now holds the real state; autosave is safe.
      collabReadyRef.current = true;
      if (!collabSeededRef.current) {
        collabSeededRef.current = true;
        // Only seed when the shared doc is still empty (nobody/no server had it);
        // if it already has content we render that as-is (no duplication).
        const frag = provider.doc.getXmlFragment('default');
        if (frag.length === 0) {
          const html = parseMarkdownToHtml(chapter.content);
          if (html) editor.commands.setContent(html, { emitUpdate: true });
        }
      }
      setCollabSynced(true); // now safe to mount the view against the real doc
    };
    const onSync = (isSynced: boolean) => { if (isSynced !== false) ready(); };
    if (provider.synced) ready();
    else provider.on('sync', onSync);
    // Fallback: never leave the writer staring at a spinner if the socket stalls.
    // Seeding from chapter.content first keeps autosave safe and shows the text.
    const timer = setTimeout(ready, 3500);
    return () => { provider.off('sync', onSync); clearTimeout(timer); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collaboration, editor, chapter.id]);

  useEffect(() => {
    if (editor && onEditorReady) {
      onEditorReady(editor);
    }
  }, [editor, onEditorReady]);

  // Keep the drop-caps class reactive to the setting. The editor's class is set
  // once at creation (editorProps), so toggling "Capitular" must update the live
  // DOM here for it to apply without remounting the editor.
  useEffect(() => {
    if (!editor) return;
    const raf = requestAnimationFrame(() => {
      if (editor.isDestroyed) return;
      try {
        editor.view.dom.classList.toggle('drop-caps', !!settings.dropCaps);
      } catch {
        /* view not mounted yet */
      }
    });
    return () => cancelAnimationFrame(raf);
  }, [editor, settings.dropCaps]);

  // Font family + spellcheck are baked into the editor's DOM attributes at
  // creation, so toggle them live here (the editor isn't recreated on a setting
  // change). This is why "changing the font" needs to be applied imperatively.
  useEffect(() => {
    if (!editor) return;
    const raf = requestAnimationFrame(() => {
      if (editor.isDestroyed) return;
      try {
        const el = editor.view.dom as HTMLElement;
        el.classList.remove('font-serif', 'font-sans', 'font-mono');
        el.classList.add(
          settings.fontFamily === 'sans'
            ? 'font-sans'
            : settings.fontFamily === 'mono'
              ? 'font-mono'
              : 'font-serif',
        );
        // Set both the IDL property and the content attribute so all browsers
        // honour the spellcheck setting on the contenteditable element.
        el.spellcheck = settings.spellcheck;
        el.setAttribute('spellcheck', String(settings.spellcheck));
      } catch {
        /* view not mounted yet */
      }
    });
    return () => cancelAnimationFrame(raf);
  }, [editor, settings.fontFamily, settings.spellcheck]);

  useEffect(() => {
    return () => {
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }
    };
  }, []);

  // Fetch story-bible entries whenever the project changes
  useEffect(() => {
    let cancelled = false;
    api
      .getBibleEntries(projectId)
      .then((res) => {
        if (!cancelled) setBibleEntries(res.entries);
      })
      .catch(() => {
        // Non-critical; silently ignore — codex highlights are best-effort
      });
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  // Push latest entries into the editor plugin via command.
  // Runs whenever entries change OR when the editor is recreated (chapter switch).
  useEffect(() => {
    if (!editor || editor.isDestroyed) return;
    editor.commands.setCodexEntries(
      bibleEntries.map((e) => ({ id: e.id, name: e.name, type: e.type })),
    );
  }, [editor, bibleEntries]);

  // Push the AI autocomplete toggle/length into the plugin live — toggling the
  // ✦ button never needs to recreate the editor (mirrors setCodexEntries above).
  useEffect(() => {
    if (!editor || editor.isDestroyed) return;
    editor.commands.setAiAutocompleteSettings({
      enabled: settings.aiAutocomplete,
      length: settings.aiAutocompleteLength,
    });
    if (!settings.aiAutocomplete) {
      setAiSuggestion(null);
      setAiPending(false);
    }
  }, [editor, settings.aiAutocomplete, settings.aiAutocompleteLength]);

  useEffect(() => {
    if (!editor || !settings.typewriterMode) return;

    const handleUpdate = () => {
      const { view } = editor;
      const { from } = view.state.selection;
      const coords = view.coordsAtPos(from);
      const editorElement = view.dom.parentElement;

      if (editorElement) {
        const editorRect = editorElement.getBoundingClientRect();
        const targetY = editorRect.height / 2;
        const currentY = coords.top - editorRect.top;
        const scrollDiff = currentY - targetY;
        editorElement.scrollBy({ top: scrollDiff, behavior: 'smooth' });
      }
    };

    editor.on('selectionUpdate', handleUpdate);
    return () => { editor.off('selectionUpdate', handleUpdate); };
  }, [editor, settings.typewriterMode]);

  const getSectionLabel = (type: string) => {
    const labels: Record<string, string> = {
      'title-page': t('sections.titlePage', 'Title Page'),
      'copyright': t('sections.copyright', 'Copyright'),
      'dedication': t('sections.dedication', 'Dedication'),
      'epigraph': t('sections.epigraph', 'Epigraph'),
      'toc': t('sections.toc', 'Table of Contents'),
      'foreword': t('sections.foreword', 'Foreword'),
      'preface': t('sections.preface', 'Preface'),
      'prologue': t('sections.prologue', 'Prologue'),
      'chapter': t('sections.chapter', 'Chapter'),
      'epilogue': t('sections.epilogue', 'Epilogue'),
      'afterword': t('sections.afterword', 'Afterword'),
      'acknowledgments': t('sections.acknowledgments', 'Acknowledgments'),
      'about-author': t('sections.aboutAuthor', 'About the Author'),
      'also-by': t('sections.alsoBy', 'Also By'),
    };
    return labels[type] || type;
  };

  const setLink = useCallback(() => {
    if (!editor) return;
    const previousUrl = editor.getAttributes('link').href;
    const url = window.prompt('URL', previousUrl || 'https://');
    if (url === null) return;
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  }, [editor]);

  const handleAddComment = useCallback(() => {
    if (!editor) return;
    const { from, to } = editor.state.selection;
    const selectedText = editor.state.doc.textBetween(from, to, ' ');
    if (!selectedText.trim()) {
      notify.warning(t('editor.selectTextFirst', 'Selecione um trecho primeiro'));
      return;
    }
    // Open the inline composer near the selection (no more native prompt).
    const coords = editor.view.coordsAtPos(to);
    const width = 264;
    const left = Math.min(Math.max(8, coords.left), window.innerWidth - width - 8);
    setCommentText('');
    setCommentDraft({
      from,
      to,
      selectedText,
      style: { position: 'fixed', top: coords.bottom + 8, left, width, zIndex: 10001 },
    });
  }, [editor, t]);

  const submitComment = useCallback(() => {
    if (!editor || !commentDraft) return;
    const text = commentText.trim();
    if (!text) return;
    const commentId = `comment-${Date.now()}`;
    editor
      .chain()
      .focus()
      .setTextSelection({ from: commentDraft.from, to: commentDraft.to })
      .setMark('comment', { commentId, commentText: text })
      .run();
    onAddComment?.(text, commentDraft.selectedText, { from: commentDraft.from, to: commentDraft.to });
    setCommentDraft(null);
    setCommentText('');
  }, [editor, commentDraft, commentText, onAddComment]);

  // AI result handler - replaces the captured selection range via insertContentAt (preserves undo)
  const handleAIResult = useCallback(
    (newText: string, range: { from: number; to: number }) => {
      if (!editor) return;
      editor
        .chain()
        .focus()
        .insertContentAt({ from: range.from, to: range.to }, newText)
        .run();
    },
    [editor],
  );

  // AI title suggestion: strips HTML prose, calls suggestTitle, persists immediately.
  // Passes sibling titles so the model can match the book's existing naming style.
  const handleSuggestTitle = useCallback(async () => {
    if (isSuggestingTitle) return;
    setIsSuggestingTitle(true);
    try {
      // Strip HTML tags to get plain prose text, capped at 1500 chars
      const plainText = chapter.content
        .replace(/<[^>]+>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
        .slice(0, 1500);
      // Fall back to current title when there is little/no content
      const text = plainText.length > 20 ? plainText : chapter.title;
      const { title: newTitle } = await api.suggestTitle(text, projectId, siblingTitles);
      // Update store + queue debounced API call (mirrors handleTitleChange in editor-view)
      onTitleChange(newTitle);
      // Immediate API persist — no need to wait for the debounce on a one-shot action
      await api.updateChapter(chapter.id, { title: newTitle });
      notify.success(t('chapterEditor.titleUpdated', 'Título atualizado'));
    } catch (error) {
      if (error instanceof AiLimitError) {
        notify.aiLimitReached('text');
      } else {
        notify.error(t('chapterEditor.titleUpdateError', 'Erro ao melhorar título'));
      }
    } finally {
      setIsSuggestingTitle(false);
    }
  }, [isSuggestingTitle, chapter.content, chapter.title, chapter.id, projectId, siblingTitles, onTitleChange, t]);

  // Insert image into editor at current cursor position (for scene illustrations)
  const handleInsertImage = useCallback(
    (src: string) => {
      if (!editor) return;
      editor
        .chain()
        .focus()
        .setImage({ src })
        .run();
    },
    [editor],
  );

  // Get selected text + range for AI menu (captured at open time)
  const getSelectedTextAndRange = useCallback((): {
    text: string;
    range: { from: number; to: number };
  } => {
    if (!editor) return { text: '', range: { from: 0, to: 0 } };
    const { from, to } = editor.state.selection;
    return {
      text: editor.state.doc.textBetween(from, to, ' '),
      range: { from, to },
    };
  }, [editor]);

  // Image handling
  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const base64 = event.target?.result as string;
      if (onImageChange) onImageChange(base64);
    };
    reader.readAsDataURL(file);
  };

  const removeImage = () => {
    if (onImageChange) onImageChange(undefined);
  };

  const highlightColors = [
    { color: '#fef08a', name: 'Yellow' },
    { color: '#bbf7d0', name: 'Green' },
    { color: '#fecaca', name: 'Red' },
    { color: '#bfdbfe', name: 'Blue' },
    { color: '#e9d5ff', name: 'Purple' },
  ];

  // a capitular ACENDE quando o capítulo ganha as primeiras palavras (nesta sessão)
  const [dcIgnite, setDcIgnite] = useState(false);
  const prevWordsRef = useRef<number>(chapter.wordCount);
  useEffect(() => {
    const prev = prevWordsRef.current;
    prevWordsRef.current = chapter.wordCount;
    if (prev === 0 && chapter.wordCount > 0) {
      setDcIgnite(true);
      const id = setTimeout(() => setDcIgnite(false), 2200);
      return () => clearTimeout(id);
    }
    // floreio: o capítulo cruzou mil palavras nesta sessão
    if (prev < 1000 && chapter.wordCount >= 1000 && prev > 0) {
      window.dispatchEvent(new CustomEvent('reformu:moment', { detail: { type: 'flourish' } }));
    }
  }, [chapter.wordCount]);

  // cerimônia: primeira vez à luz de vela (modo papel), uma vez na vida
  useEffect(() => {
    if (settings.canvasTheme !== 'paper') return;
    if (localStorage.getItem('moment-paperlight')) return;
    localStorage.setItem('moment-paperlight', '1');
    window.dispatchEvent(new CustomEvent('reformu:moment', { detail: { type: 'paperlight' } }));
  }, [settings.canvasTheme]);

  // MODO PAPEL = MODO CLARO da plataforma. O editor vira pergaminho (como a
  // demo) sempre que o tema resolvido é claro — não só no toggle canvasTheme.
  // Assim a "letra grande" (drop-cap), a tinta e o âmbar mudam de verdade no claro.
  const resolvedLight =
    settings.theme === 'light' ||
    (settings.theme === 'system' &&
      typeof matchMedia !== 'undefined' &&
      !matchMedia('(prefers-color-scheme: dark)').matches);
  const paperMode = settings.canvasTheme === 'paper' || resolvedLight;

  if (!editor) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <MoonLoader size={30} />
      </div>
    );
  }

  // "AI is thinking" indicator — a tiny dot right next to the caret while a
  // suggestion request is in flight. Never shown together with the pill
  // (they're mutually exclusive: pending means no suggestion yet).
  let aiPendingIndicatorStyle: React.CSSProperties | null = null;
  if (aiPending && !aiSuggestion) {
    try {
      const coords = editor.view.coordsAtPos(editor.state.selection.from);
      aiPendingIndicatorStyle = {
        position: 'fixed',
        top: (coords.top + coords.bottom) / 2 - 3,
        left: coords.right + 5,
        zIndex: 9997,
      };
    } catch {
      /* view not mounted yet — skip this frame */
    }
  }

  return (
    <div className={cn(
      'relative flex-1 flex flex-col overflow-hidden transition-colors duration-300',
      dcIgnite && 'dc-ignite',
      paperMode && 'paper-canvas',
      focusMode ? 'bg-ink-950' : 'bg-background'
    )}>
      {/* Live particle layer — the chosen effect rises from the caret as you write */}
      <EditorSmoke ref={smokeRef} type={settings.writingEffect} paper={paperMode} />
      <div className="flex-1 overflow-auto">
        <motion.div
          key={chapter.id}
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.34, ease: [0.22, 1, 0.36, 1] }}
          className={cn(
            'max-w-[680px] mx-auto px-5 sm:px-8 md:px-12',
            focusMode ? 'py-20' : 'py-12',
            // Paper mode (= modo claro): the writing page becomes a warm vellum
            // sheet. CSS in styles.css handles it.
            paperMode && 'editor-paper'
          )}
          style={{
            fontSize: `${settings.fontSize}px`,
            lineHeight: settings.lineHeight === 'compact' ? 1.4 : settings.lineHeight === 'relaxed' ? 2 : 1.7,
          }}
        >
          {/* Chapter Number — eyebrow de brasa em romano, como na demo */}
          {settings.showChapterNumber && chapterNumber !== undefined && chapter.sectionType === 'chapter' && (
            <div className="mb-4 mt-10 text-center">
              <span
                className="v-chnum-color font-mono text-[10px] md:text-[11px] font-medium tracking-[0.4em] uppercase"
                style={{ color: 'var(--ember)' }}
              >
                {t('editor.chapterEyebrow', 'Capítulo')} {toRoman(chapterNumber)}
              </span>
            </div>
          )}

          {/* Section Type Badge for non-chapters */}
          {chapter.sectionType && chapter.sectionType !== 'chapter' && (
            <div className="text-center mb-6 mt-4">
              <Badge variant="secondary" className="text-xs tracking-wider uppercase px-4 py-1">
                {getSectionLabel(chapter.sectionType)}
              </Badge>
            </div>
          )}

          {/* Image upload for About the Author page */}
          {supportsImage && (
            <div className="mb-12 flex flex-col items-center gap-6">
              {/* Image Container with Shape */}
              <div
                ref={imageContainerRef}
                className="relative group cursor-pointer"
                onClick={() => !chapter.image && fileInputRef.current?.click()}
              >
                {/* Shape Container - this rotates */}
                <div
                  className="overflow-hidden transition-all duration-300 shadow-lg"
                  style={{
                    transform: `rotate(${shapeRotation}deg)`,
                    width: imageAspect === 'landscape' ? '380px' : imageAspect === 'portrait' ? '280px' : '320px',
                    height: imageAspect === 'landscape' ? '280px' : imageAspect === 'portrait' ? '380px' : '320px',
                    borderRadius: imageShape === 'circle' ? '50%' :
                                  imageShape === 'diamond' ? '0' :
                                  imageShape === 'hexagon' ? '0' :
                                  imageShape === 'triangle' ? '0' :
                                  `${borderRadius}px`,
                    clipPath: imageShape === 'triangle' ? 'polygon(50% 0%, 0% 100%, 100% 100%)' :
                              imageShape === 'hexagon' ? 'polygon(25% 0%, 75% 0%, 100% 50%, 75% 100%, 25% 100%, 0% 50%)' :
                              imageShape === 'diamond' ? 'polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)' :
                              'none',
                  }}
                >
                  {/* Image - counter-rotates to stay upright, with zoom and filter */}
                  {chapter.image ? (
                    <div className="relative w-full h-full">
                      <img
                        src={chapter.image}
                        alt="Author"
                        className="w-full h-full object-cover transition-transform duration-300"
                        style={{
                          transform: `rotate(${-shapeRotation}deg) scale(${imageZoom})`,
                          filter: imageFilter === 'grayscale' ? 'grayscale(100%)' :
                                  imageFilter === 'sepia' ? 'sepia(80%)' :
                                  imageFilter === 'contrast' ? 'contrast(130%) saturate(130%)' :
                                  imageFilter === 'brightness' ? 'brightness(120%)' :
                                  'none',
                        }}
                      />
                      {/* Gradient overlays */}
                      {imageFilter === 'gradient-dark' && (
                        <div
                          className="absolute inset-0 pointer-events-none"
                          style={{
                            background: 'radial-gradient(circle, transparent 40%, rgba(0,0,0,0.6) 100%)',
                            transform: `rotate(${-shapeRotation}deg)`,
                          }}
                        />
                      )}
                      {imageFilter === 'gradient-light' && (
                        <div
                          className="absolute inset-0 pointer-events-none"
                          style={{
                            background: 'linear-gradient(to top, rgba(255,255,255,0.7) 0%, transparent 60%)',
                            transform: `rotate(${-shapeRotation}deg)`,
                          }}
                        />
                      )}
                    </div>
                  ) : (
                    <div
                      className="w-full h-full flex flex-col items-center justify-center gap-3 bg-muted/50 border-2 border-dashed border-border text-muted-foreground hover:border-primary hover:text-primary transition-colors"
                      style={{ transform: `rotate(${-shapeRotation}deg)` }}
                    >
                      <ImagePlus size={40} />
                      <span className="text-sm font-medium">Add Photo</span>
                    </div>
                  )}
                </div>

                {/* Edit button */}
                {chapter.image && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (imageContainerRef.current) {
                        const rect = imageContainerRef.current.getBoundingClientRect();
                        setPopoverPosition({
                          top: rect.bottom + 16,
                          left: rect.left + rect.width / 2 - 180,
                        });
                      }
                      setShowImageControls(true);
                    }}
                    className="absolute -bottom-3 left-1/2 -translate-x-1/2 px-3 py-1.5 bg-card text-foreground text-xs font-medium rounded-full shadow-lg border border-border hover:bg-muted transition-all opacity-0 group-hover:opacity-100 z-10"
                  >
                    Edit
                  </button>
                )}
              </div>

              {/* Controls Toggle */}
              <button
                onClick={() => {
                  if (!showImageControls && imageContainerRef.current) {
                    const rect = imageContainerRef.current.getBoundingClientRect();
                    setPopoverPosition({
                      top: rect.bottom + 16,
                      left: rect.left + rect.width / 2 - 180,
                    });
                  }
                  setShowImageControls(!showImageControls);
                }}
                className="text-xs text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1"
              >
                {showImageControls ? 'Hide' : 'Edit'} image
                <ChevronDown size={12} className={cn('transition-transform', showImageControls && 'rotate-180')} />
              </button>

              {/* Image Controls Popover - Fixed positioning */}
              {showImageControls && (
                <>
                  <div className="fixed inset-0 z-[100]" onClick={() => setShowImageControls(false)} />
                  <div
                    className="fixed z-[101] bg-popover rounded-xl border border-border shadow-2xl p-4 w-[360px] animate-fade-in"
                    style={{ top: popoverPosition.top, left: Math.max(16, popoverPosition.left) }}
                  >
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="text-sm font-semibold">Image Editor</h4>
                      <button onClick={() => setShowImageControls(false)} className="text-muted-foreground hover:text-foreground">
                        <X size={16} />
                      </button>
                    </div>

                    <div className="space-y-4">
                      {/* Zoom Control */}
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <label className="text-xs font-medium text-muted-foreground">Zoom</label>
                          <span className="text-xs text-muted-foreground">{Math.round(imageZoom * 100)}%</span>
                        </div>
                        <input
                          type="range"
                          min="1"
                          max="3"
                          step="0.1"
                          value={imageZoom}
                          onChange={(e) => setImageZoom(Number(e.target.value))}
                          className="w-full h-2 bg-muted rounded-full appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-primary"
                        />
                      </div>

                      {/* Shape & Aspect in row */}
                      <div className="flex gap-3">
                        <div className="flex-1">
                          <label className="text-xs font-medium text-muted-foreground mb-2 block">Shape</label>
                          <div className="flex items-center gap-0.5 bg-muted rounded-lg p-1">
                            {[
                              { shape: 'square' as const, icon: Square },
                              { shape: 'circle' as const, icon: Circle },
                              { shape: 'hexagon' as const, icon: Hexagon },
                              { shape: 'diamond' as const, icon: Square, rotate: 45 },
                            ].map(({ shape, icon: Icon, rotate }) => (
                              <button
                                key={shape}
                                onClick={() => setImageShape(shape)}
                                className={cn(
                                  'p-1.5 rounded-md transition-colors flex-1 flex justify-center',
                                  imageShape === shape
                                    ? 'bg-primary text-primary-foreground'
                                    : 'text-muted-foreground hover:text-foreground'
                                )}
                              >
                                <Icon size={14} style={{ transform: rotate ? `rotate(${rotate}deg)` : undefined }} />
                              </button>
                            ))}
                          </div>
                        </div>
                        <div className="flex-1">
                          <label className="text-xs font-medium text-muted-foreground mb-2 block">Aspect</label>
                          <div className="flex items-center gap-0.5 bg-muted rounded-lg p-1">
                            {[
                              { aspect: 'square' as const, icon: Square },
                              { aspect: 'portrait' as const, icon: RectangleVertical },
                              { aspect: 'landscape' as const, icon: RectangleHorizontal },
                            ].map(({ aspect, icon: Icon }) => (
                              <button
                                key={aspect}
                                onClick={() => setImageAspect(aspect)}
                                className={cn(
                                  'p-1.5 rounded-md transition-colors flex-1 flex justify-center',
                                  imageAspect === aspect
                                    ? 'bg-primary text-primary-foreground'
                                    : 'text-muted-foreground hover:text-foreground'
                                )}
                              >
                                <Icon size={14} />
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>

                      {/* Image Filters */}
                      <div>
                        <label className="text-xs font-medium text-muted-foreground mb-2 block">Filter</label>
                        <div className="grid grid-cols-4 gap-2">
                          {[
                            { filter: 'none' as const, label: 'None', preview: 'bg-gradient-to-br from-blue-400 to-purple-400' },
                            { filter: 'grayscale' as const, label: 'B&W', preview: 'bg-gradient-to-br from-gray-400 to-gray-600' },
                            { filter: 'sepia' as const, label: 'Sepia', preview: 'bg-gradient-to-br from-amber-400 to-orange-500' },
                            { filter: 'contrast' as const, label: 'Vivid', preview: 'bg-gradient-to-br from-red-500 to-yellow-400' },
                            { filter: 'brightness' as const, label: 'Bright', preview: 'bg-gradient-to-br from-white to-blue-200' },
                            { filter: 'gradient-dark' as const, label: 'Vignette', preview: 'bg-gradient-radial from-transparent to-black' },
                            { filter: 'gradient-light' as const, label: 'Fade', preview: 'bg-gradient-to-t from-white/80 to-transparent' },
                          ].map(({ filter, label, preview }) => (
                            <button
                              key={filter}
                              onClick={() => setImageFilter(filter)}
                              className={cn(
                                'flex flex-col items-center gap-1 p-2 rounded-lg border-2 transition-all',
                                imageFilter === filter
                                  ? 'border-primary bg-primary/10'
                                  : 'border-transparent hover:bg-muted'
                              )}
                            >
                              <div className={cn('w-8 h-8 rounded-md', preview)} />
                              <span className="text-[10px] text-muted-foreground">{label}</span>
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Rotation & Radius */}
                      <div className="flex gap-3">
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-2">
                            <label className="text-xs font-medium text-muted-foreground">Rotate</label>
                            <span className="text-xs text-muted-foreground">{shapeRotation}°</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <input
                              type="range"
                              min="0"
                              max="360"
                              step="15"
                              value={shapeRotation}
                              onChange={(e) => setShapeRotation(Number(e.target.value))}
                              className="flex-1 h-2 bg-muted rounded-full appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-primary"
                            />
                            <button
                              onClick={() => setShapeRotation((r) => (r + 45) % 360)}
                              className="p-1.5 rounded-md bg-muted hover:bg-muted/80 text-muted-foreground"
                            >
                              <RotateCw size={12} />
                            </button>
                          </div>
                        </div>
                        {imageShape === 'square' && (
                          <div className="flex-1">
                            <div className="flex items-center justify-between mb-2">
                              <label className="text-xs font-medium text-muted-foreground">Radius</label>
                              <span className="text-xs text-muted-foreground">{borderRadius}px</span>
                            </div>
                            <input
                              type="range"
                              min="0"
                              max="80"
                              step="4"
                              value={borderRadius}
                              onChange={(e) => setBorderRadius(Number(e.target.value))}
                              className="w-full h-2 bg-muted rounded-full appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-primary"
                            />
                          </div>
                        )}
                      </div>

                      {/* Action buttons */}
                      <div className="flex gap-2 pt-2 border-t border-border">
                        <button
                          onClick={() => fileInputRef.current?.click()}
                          className="flex-1 py-2 px-3 rounded-lg bg-muted text-sm font-medium text-foreground hover:bg-muted/80 transition-colors"
                        >
                          {chapter.image ? 'Change' : 'Upload'}
                        </button>
                        {chapter.image && (
                          <button
                            onClick={removeImage}
                            className="py-2 px-3 rounded-lg bg-destructive/10 text-destructive text-sm font-medium hover:bg-destructive/20 transition-colors"
                          >
                            Remove
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </>
              )}

              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                className="hidden"
              />
            </div>
          )}

          {/* Chapter Title */}
          <div className="relative mb-10 min-w-0">
            <input
              ref={titleRef}
              type="text"
              value={chapter.title}
              onChange={(e) => onTitleChange(e.target.value)}
              placeholder={t('editor.chapterPlaceholder', 'Chapter Title')}
              className={cn(
                'w-full min-w-0 font-serif font-medium bg-transparent border-none outline-none text-center',
                'text-[27px] md:text-[34px] leading-tight transition-colors duration-300 px-8',
                focusMode
                  ? 'text-white/80 placeholder:text-white/25 caret-white/60'
                  : 'text-foreground placeholder:text-muted-foreground/50'
              )}
            />
            {/* Improve title with AI */}
            <button
              type="button"
              onClick={handleSuggestTitle}
              disabled={isSuggestingTitle}
              title={t('chapterEditor.improveTitleWithAi', 'Melhorar título com IA')}
              aria-label={t('chapterEditor.improveTitleWithAi', 'Melhorar título com IA')}
              className={cn(
                'absolute right-0 top-1/2 -translate-y-1/2',
                'inline-flex items-center justify-center w-7 h-7 rounded-md',
                'transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                'disabled:opacity-40 disabled:cursor-not-allowed',
                focusMode
                  ? 'text-white/30 hover:text-white/70 hover:bg-white/10'
                  : 'text-muted-foreground/50 hover:text-primary hover:bg-primary/10'
              )}
            >
              {isSuggestingTitle
                ? <Loader2 size={14} className="animate-spin" />
                : <Sparkles size={14} />}
            </button>
          </div>

          {/* Bubble Menu */}
          <BubbleMenu
            editor={editor}
            className="bubble-menu-container hidden md:block bg-popover border border-border rounded-[10px] shadow-[0_10px_30px_rgba(0,0,0,0.55)] overflow-visible z-[9999]"
          >
            <div className="flex flex-col divide-y divide-border">
              {/* Row 1: Text formatting */}
              <div className="flex items-center gap-0.5 px-2 py-1.5">
                <BubbleBtn
                  onClick={() => editor.chain().focus().toggleBold().run()}
                  active={editor.isActive('bold')}
                  title="Bold (Ctrl+B)"
                >
                  <Bold size={15} />
                </BubbleBtn>
                <BubbleBtn
                  onClick={() => editor.chain().focus().toggleItalic().run()}
                  active={editor.isActive('italic')}
                  title="Italic (Ctrl+I)"
                >
                  <Italic size={15} />
                </BubbleBtn>
                <BubbleBtn
                  onClick={() => editor.chain().focus().toggleUnderline().run()}
                  active={editor.isActive('underline')}
                  title="Underline (Ctrl+U)"
                >
                  <UnderlineIcon size={15} />
                </BubbleBtn>
                <BubbleBtn
                  onClick={() => editor.chain().focus().toggleStrike().run()}
                  active={editor.isActive('strike')}
                  title="Strikethrough"
                >
                  <Strikethrough size={15} />
                </BubbleBtn>

                <Separator />

                <div className="relative">
                  <BubbleBtn
                    onClick={() => setShowColorPicker(!showColorPicker)}
                    active={editor.isActive('highlight')}
                    title="Highlight"
                  >
                    <Highlighter size={15} />
                  </BubbleBtn>
                  {showColorPicker && (
                    <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 p-2 bg-card border border-border rounded-lg shadow-xl flex gap-1.5 z-[10000]">
                      {highlightColors.map((c) => (
                        <button
                          key={c.color}
                          onClick={() => {
                            editor.chain().focus().toggleHighlight({ color: c.color }).run();
                            setShowColorPicker(false);
                          }}
                          className="w-6 h-6 rounded-full border-2 border-card shadow-md hover:scale-110 active:scale-95 transition-transform"
                          style={{ backgroundColor: c.color }}
                          title={c.name}
                          aria-label={`Highlight ${c.name}`}
                        />
                      ))}
                      <button
                        onClick={() => {
                          editor.chain().focus().unsetHighlight().run();
                          setShowColorPicker(false);
                        }}
                        className="w-6 h-6 rounded-full border-2 border-border flex items-center justify-center text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
                        title="Remove highlight"
                        aria-label="Remove highlight"
                      >
                        <X size={10} />
                      </button>
                    </div>
                  )}
                </div>

                <BubbleBtn onClick={setLink} active={editor.isActive('link')} title="Link">
                  <LinkIcon size={15} />
                </BubbleBtn>
              </div>

              {/* Row 2: Block formatting */}
              <div className="flex items-center gap-0.5 px-2 py-1.5">
                <BubbleBtn
                  onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
                  active={editor.isActive('heading', { level: 2 })}
                  title="Heading 2"
                >
                  <Heading2 size={15} />
                </BubbleBtn>
                <BubbleBtn
                  onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
                  active={editor.isActive('heading', { level: 3 })}
                  title="Heading 3"
                >
                  <Heading3 size={15} />
                </BubbleBtn>

                <Separator />

                <BubbleBtn
                  onClick={() => editor.chain().focus().toggleBlockquote().run()}
                  active={editor.isActive('blockquote')}
                  title="Quote"
                >
                  <Quote size={15} />
                </BubbleBtn>
                <BubbleBtn
                  onClick={() => editor.chain().focus().toggleBulletList().run()}
                  active={editor.isActive('bulletList')}
                  title="Bullet List"
                >
                  <List size={15} />
                </BubbleBtn>
                <BubbleBtn
                  onClick={() => editor.chain().focus().toggleOrderedList().run()}
                  active={editor.isActive('orderedList')}
                  title="Numbered List"
                >
                  <ListOrdered size={15} />
                </BubbleBtn>

                <Separator />

                <BubbleBtn
                  onClick={() => editor.chain().focus().unsetAllMarks().run()}
                  title="Clear Formatting"
                >
                  <RemoveFormatting size={15} />
                </BubbleBtn>
              </div>

              {/* Row 3: Alignment & Comments */}
              <div className="flex items-center gap-0.5 px-2 py-1.5">
                <BubbleBtn
                  onClick={() => editor.chain().focus().setTextAlign('left').run()}
                  active={editor.isActive({ textAlign: 'left' })}
                  title="Align Left"
                >
                  <AlignLeft size={15} />
                </BubbleBtn>
                <BubbleBtn
                  onClick={() => editor.chain().focus().setTextAlign('center').run()}
                  active={editor.isActive({ textAlign: 'center' })}
                  title="Align Center"
                >
                  <AlignCenter size={15} />
                </BubbleBtn>
                <BubbleBtn
                  onClick={() => editor.chain().focus().setTextAlign('right').run()}
                  active={editor.isActive({ textAlign: 'right' })}
                  title="Align Right"
                >
                  <AlignRight size={15} />
                </BubbleBtn>
                <BubbleBtn
                  onClick={() => editor.chain().focus().setTextAlign('justify').run()}
                  active={editor.isActive({ textAlign: 'justify' })}
                  title="Justify"
                >
                  <AlignJustify size={15} />
                </BubbleBtn>

                <div className="flex-1" />

                <BubbleBtn
                  onClick={handleAddComment}
                  active={editor.isActive('comment')}
                  title="Add Comment"
                  className="bg-accent/10 text-accent hover:bg-accent/20"
                >
                  <MessageSquarePlus size={15} />
                </BubbleBtn>

                {/* AI Assistant Button */}
                <AIMenuButton
                  onGetSelectionSnapshot={getSelectedTextAndRange}
                  onApplyResult={handleAIResult}
                  onInsertImage={handleInsertImage}
                  showAIMenu={showAIMenu}
                  setShowAIMenu={setShowAIMenu}
                  projectId={projectId}
                  chapterId={chapter.id}
                />
              </div>
            </div>
          </BubbleMenu>

          {/* Inline comment composer (anchored near the selection) */}
          {commentDraft && (
            <>
              <div
                className="fixed inset-0 z-[10000]"
                onClick={() => setCommentDraft(null)}
              />
              <div
                style={commentDraft.style}
                className="rounded-xl border border-border bg-card p-3 shadow-2xl"
              >
                <p className="mb-1.5 line-clamp-2 rounded-md bg-accent/10 px-2 py-1 text-[11px] italic text-muted-foreground">
                  “{commentDraft.selectedText}”
                </p>
                <textarea
                  autoFocus
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      submitComment();
                    }
                    if (e.key === 'Escape') setCommentDraft(null);
                  }}
                  rows={3}
                  placeholder={t('editor.enterComment', 'Escreva um comentário…')}
                  className="w-full resize-none rounded-lg border border-border bg-muted/40 px-2.5 py-1.5 text-sm text-foreground placeholder:text-muted-foreground/60 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                />
                <div className="mt-2 flex items-center justify-end gap-2">
                  <button
                    onClick={() => setCommentDraft(null)}
                    className="rounded-lg px-2.5 py-1 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                  >
                    {t('common.cancel', 'Cancelar')}
                  </button>
                  <button
                    onClick={submitComment}
                    disabled={!commentText.trim()}
                    className="rounded-lg bg-primary px-3 py-1 text-xs font-semibold text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-40"
                  >
                    {t('editor.comment', 'Comentar')}
                  </button>
                </div>
              </div>
            </>
          )}

          {/* Editor Content — in collab mode the view mounts only after the Y.Doc
              has synced (or the 3.5s fallback), so it never renders against an
              empty doc and blanks on mobile. */}
          {(!collaboration || collabSynced) ? (
            <div className={cn(
              focusMode && '[&_.ProseMirror]:text-white/90 [&_.is-editor-empty]:before:text-white/30'
            )}>
              <EditorContent editor={editor} />
            </div>
          ) : (
            <div className="min-h-[50vh] flex items-center justify-center">
              <Loader2 className="animate-spin text-muted-foreground/50" size={22} />
            </div>
          )}

          {/* Chapter Notes */}
          {chapter.notes && !focusMode && (
            <div className="mt-10 p-4 bg-amber-500/10 border border-amber-500/20 rounded-lg">
              <h4 className="text-sm font-medium text-amber-600 dark:text-amber-400 mb-2">
                {t('editor.notes', 'Notes')}
              </h4>
              <p className="text-sm text-amber-700 dark:text-amber-300">{chapter.notes}</p>
            </div>
          )}
        </motion.div>
      </div>

      {/* "AI is thinking" indicator — subtle pulsing dot near the caret while a
          suggestion request is in flight. Static (no animation) under
          prefers-reduced-motion via the motion-safe: variant. */}
      {aiPendingIndicatorStyle && (
        <span
          aria-hidden="true"
          style={aiPendingIndicatorStyle}
          className={cn(
            'pointer-events-none h-[7px] w-[7px] rounded-full motion-safe:animate-pulse',
            paperMode ? 'bg-[#B3702A]/60' : 'bg-primary/60',
          )}
        />
      )}

      {/* Sem pílula de "Tab aceitar" — no desktop o Tab já aceita; no mobile a
          própria sugestão (ghost text) é tocável pra aceitar. Nada por cima do texto. */}

      {/* Mobile persistent formatting toolbar — replaces the selection bubble on small screens */}
      <MobileEditorToolbar editor={editor} paperMode={paperMode} />

      {/* Codex Inline preview card — rendered via fixed positioning, zero cost when entries empty */}
      <CodexPreviewCard entries={bibleEntries} onOpenEntry={setDossierEntry} />

      {/* Full read-only dossier — opened by clicking a highlighted Codex entity */}
      <EntryDossier
        entry={dossierEntry}
        typeLabel={(e) => getEntryTypeLabel(t, e.type)}
        onClose={() => setDossierEntry(null)}
      />

    </div>
  );
}

// Separator component
function Separator() {
  return <div className="w-px h-4 bg-border mx-1" />;
}

// Bubble Menu Button Component
function BubbleBtn({
  onClick,
  active = false,
  children,
  title,
  className,
}: {
  onClick: () => void;
  active?: boolean;
  children: React.ReactNode;
  title?: string;
  className?: string;
}) {
  return (
    <button
      type="button"
      onMouseDown={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onClick();
      }}
      title={title}
      className={cn(
        'p-1.5 rounded-md transition-all duration-100 select-none',
        active
          ? 'bg-primary text-primary-foreground'
          : 'text-foreground/70 hover:bg-muted hover:text-foreground',
        className
      )}
    >
      {children}
    </button>
  );
}

// AI Menu Button with smart positioning
function AIMenuButton({
  onGetSelectionSnapshot,
  onApplyResult,
  onInsertImage,
  showAIMenu,
  setShowAIMenu,
  projectId,
  chapterId,
}: {
  onGetSelectionSnapshot: () => { text: string; range: { from: number; to: number } };
  onApplyResult: (result: string, range: { from: number; to: number }) => void;
  onInsertImage: (src: string) => void;
  showAIMenu: boolean;
  setShowAIMenu: (show: boolean) => void;
  projectId: string;
  chapterId: string;
}) {
  const { t } = useTranslation();
  const buttonRef = useRef<HTMLDivElement>(null);
  // Snapshot captured once when the menu opens — prevents range loss when backdrop renders
  const [snapshot, setSnapshot] = useState<{
    text: string;
    range: { from: number; to: number };
  } | null>(null);
  const [menuStyle, setMenuStyle] = useState<React.CSSProperties>({});

  const handleClick = useCallback(() => {
    const { text, range } = onGetSelectionSnapshot();
    if (!text.trim()) {
      notify.warning(t('editor.selectTextFirst', 'Please select some text first'));
      return;
    }

    if (!showAIMenu) {
      // Capture snapshot before backdrop can clear selection
      setSnapshot({ text, range });

      // Calculate menu position
      if (buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect();
        const menuHeight = 360; // compact AIMenu estimate
        const menuWidth = 224; // w-56
        const spaceAbove = rect.top;
        const spaceBelow = window.innerHeight - rect.bottom;
        const spaceRight = window.innerWidth - rect.right;

        const openBelow = spaceAbove < menuHeight && spaceBelow > spaceAbove;

        let left = rect.left;
        if (spaceRight < menuWidth) {
          left = rect.right - menuWidth;
        }

        // Cap the menu to the available vertical space so it NEVER spills off
        // screen — the body scrolls internally and the last option stays reachable.
        const available = (openBelow ? spaceBelow : spaceAbove) - 16;

        setMenuStyle({
          position: 'fixed',
          top: openBelow ? rect.bottom + 8 : undefined,
          bottom: openBelow ? undefined : window.innerHeight - rect.top + 8,
          left: Math.max(8, left),
          maxHeight: Math.max(220, available),
          zIndex: 10001,
        });
      }
    }

    setShowAIMenu(!showAIMenu);
  }, [onGetSelectionSnapshot, showAIMenu, setShowAIMenu, t]);

  const handleClose = useCallback(() => {
    setShowAIMenu(false);
    setSnapshot(null);
  }, [setShowAIMenu]);

  return (
    <div ref={buttonRef} className="relative">
      <BubbleBtn
        onClick={handleClick}
        active={showAIMenu}
        title="AI Ghostwriter"
        className="bg-primary/10 text-primary hover:bg-primary/20"
      >
        <Sparkles size={15} />
      </BubbleBtn>
      {showAIMenu && snapshot && (
        <>
          {/* Backdrop to close on click outside */}
          <div
            className="fixed inset-0 z-[10000]"
            onClick={handleClose}
          />
          {/* Menu with smart positioning */}
          <div style={menuStyle}>
            <AIMenu
              selectedText={snapshot.text}
              selectionRange={snapshot.range}
              projectId={projectId}
              chapterId={chapterId}
              onApplyResult={onApplyResult}
              onInsertImage={onInsertImage}
              onClose={handleClose}
            />
          </div>
        </>
      )}
    </div>
  );
}
